import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Typography, Card, Table, Tag, Collapse, Button } from 'antd';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

interface PTMPredictionResultProps extends ResultComponentProps {
  // 继承 ResultComponentProps，已包含 onDeleteResult
}

interface PredictionSite {
  key: React.Key;
  position: number;
  residue: string;
  score: number;
  prediction: string;
}

interface SequenceData {
  sequenceId: string;
  sites: PredictionSite[];
  originalSequence?: string;
}

const PTMPredictionResult: React.FC<PTMPredictionResultProps> = ({ 
  data, 
  title = 'PTM预测结果',
  sequence, // 接收 sequence prop
  onDeleteResult, // 接收 onDeleteResult prop
  multiResults,
  ...rest
}) => {
  // 动态获取 PTM 类型
  const getPTMType = (): string => {
    // 从统一格式中提取 PTM 类型 - 修复：支持新的结果结构
    const ptmType = data?.ptm_type || 
                   data?.artifacts?.ptm_type || 
                   data?.results?.[0]?.predictions?.[0]?.ptm_type ||
                   data?.results?.[0]?.result?.ptm_type || 
                   data?.parameters?.ptm_type ||
                   'PTM';
    
    console.log('🔍 PTM getPTMType - 提取的PTM类型:', {
      ptmType,
      'data.ptm_type': data?.ptm_type,
      'data.artifacts?.ptm_type': data?.artifacts?.ptm_type,
      'data.results[0]?.predictions[0]?.ptm_type': data?.results?.[0]?.predictions?.[0]?.ptm_type,
      'data.parameters?.ptm_type': data?.parameters?.ptm_type
    });
    
    return ptmType;
  };

  // 解析原始序列数据
  const parseOriginalSequences = (): { [key: string]: string } => {
    const sequenceMap: { [key: string]: string } = {};
    
    // 调试日志
    console.log('PTM parseOriginalSequences - data structure:', data);
    console.log('PTM parseOriginalSequences - sequence prop:', sequence);
    
    // 尝试多种方式获取序列数据
    let fastaContent = sequence || data.sequence;
    
    // 如果没有找到序列，尝试从多个可能的位置获取
    if (!fastaContent) {
      // 从统一格式的任务参数中提取序列 - 修复：直接访问顶层parameters
      const taskSequence = data?.parameters?.sequence || 
                          data?.artifacts?.sequence ||
                          data?.meta?.sequence ||
                          // 前端页面可能直接在 data 中包含序列信息
                          data?.sequence ||
                          // 检查 results 数组中是否有序列信息
                          data?.results?.[0]?.sequence ||
                          '';
      console.log('🔍 PTM parseOriginalSequences - taskSequence:', taskSequence);
      console.log('🔍 PTM parseOriginalSequences - data.parameters:', data?.parameters);
      console.log('🔍 PTM parseOriginalSequences - data.sequence:', data?.sequence);
      console.log('🔍 PTM parseOriginalSequences - data.results?.[0]:', data?.results?.[0]);
      
      if (taskSequence) {
        // 如果序列不是FASTA格式，构造一个简单的FASTA
        if (!taskSequence.startsWith('>')) {
          const sequenceName = data?.parameters?.name || 'Analysis';
          fastaContent = `>${sequenceName}\n${taskSequence}`;
        } else {
          fastaContent = taskSequence;
        }
      }
    }
    
    // 如果还是没有序列，尝试从新的后端结果结构获取
    if (!fastaContent) {
      // 新的多序列结构：从 results 数组中构建FASTA
      if (data?.results && Array.isArray(data.results)) {
        let multiSeqFasta = '';
        data.results.forEach((seqResult: any) => {
          if (seqResult.sequence_id && seqResult.sequence) {
            multiSeqFasta += `>${seqResult.sequence_id}\n${seqResult.sequence}\n`;
          }
        });
        if (multiSeqFasta) {
          fastaContent = multiSeqFasta;
          console.log('🔍 PTM parseOriginalSequences - 使用多序列结果:', fastaContent?.substring(0, 100));
        }
      }
      
      // 兜底：从parameters获取
      if (!fastaContent) {
        const resultSequence = data?.parameters?.sequence;
        const resultName = data?.parameters?.name || 'Analysis';
        
        if (resultSequence) {
          fastaContent = `>${resultName}\n${resultSequence}`;
          console.log('🔍 PTM parseOriginalSequences - 使用parameters序列:', fastaContent?.substring(0, 100));
        }
      }
    }
    
    console.log('PTM parseOriginalSequences - final fastaContent:', fastaContent);
    
    if (fastaContent) {
      // 如果不是FASTA格式（纯序列），直接使用默认ID
      if (!fastaContent.includes('>')) {
        sequenceMap['PTM_Prediction'] = fastaContent.trim();
      } else {
        // 处理FASTA格式
        const lines = fastaContent.split('\n');
        let currentId = '';
        let currentSequence = '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('>')) {
            // 保存前一个序列
            if (currentId && currentSequence) {
              sequenceMap[currentId] = currentSequence;
            }
            // 开始新序列
            currentId = trimmedLine.substring(1).trim();
            currentSequence = '';
          } else if (trimmedLine && currentId) {
            currentSequence += trimmedLine;
          }
        }
        
        // 保存最后一个序列
        if (currentId && currentSequence) {
          sequenceMap[currentId] = currentSequence;
        }
      }
    }
    
    return sequenceMap;
  };

  // 解析结构化的PTM数据（支持新的多序列格式）
  const parseStructuredPTMData = (data: any): SequenceData[] => {
    console.log('🔍 PTM parseStructuredPTMData - 解析结构化数据:', {
      data,
      hasResults: !!(data?.results),
      resultsLength: data?.results ? data.results.length : 0,
      resultsType: typeof data?.results,
      isArray: Array.isArray(data?.results),
      firstResultKeys: data?.results?.[0] ? Object.keys(data.results[0]) : []
    });
    
    const sequences: SequenceData[] = [];
    const originalSequences = parseOriginalSequences();
    
    // 新的多序列结构
    if (data.results && Array.isArray(data.results)) {
      data.results.forEach((seqResult: any, index: number) => {
        const sequenceId = seqResult.sequence_id || `PTM_Prediction_${index + 1}`;
        
        // 直接从新结构获取序列
        let originalSequence = seqResult.sequence || 
                              originalSequences[sequenceId] || 
                              originalSequences['Analysis'] ||
                              originalSequences['PTM_Prediction'] || 
                              data?.parameters?.sequence || 
                              '';
        
        console.log('🔍 PTM parseStructuredPTMData - 序列信息:', {
          sequenceId,
          originalSequence: originalSequence ? originalSequence.substring(0, 50) + '...' : 'N/A',
          directFromResult: seqResult.sequence ? seqResult.sequence.substring(0, 50) + '...' : 'N/A',
          originalSequencesKeys: Object.keys(originalSequences),
          predictionsCount: seqResult.predictions ? seqResult.predictions.length : 0
        });
        
        // 转换预测结果格式 - 支持新的预测数据结构
        const predictions = seqResult.predictions || seqResult.result?.predictions || seqResult.result?.sites || [];
        const sites: PredictionSite[] = predictions.map((pred: any, idx: number) => ({
          key: idx,
          position: pred.position || pred.pos || idx + 1,
          residue: pred.residue || pred.amino_acid || 'X',
          score: pred.score || pred.probability || 0,
          prediction: pred.prediction || pred.ptm_type || 'None'
        }));
        
        sequences.push({
          sequenceId,
          sites,
          originalSequence
        });
        
        console.log(`✅ PTM parseStructuredPTMData - 成功解析序列 ${sequenceId}:`, sites.length, '个位点');
      });
    }
    
    return sequences;
  };

  // 解析预测结果，支持多序列
  const parsePredictionResults = (): SequenceData[] => {
    console.log('🔍 PTM parsePredictionResults - 完整数据结构:', {
      data,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
      hasArtifacts: !!(data?.artifacts),
      artifactsKeys: data?.artifacts ? Object.keys(data.artifacts) : [],
      hasResults: !!(data?.results),
      resultsLength: data?.results ? data.results.length : 0,
      firstResult: data?.results?.[0],
      resultsIsArray: Array.isArray(data?.results),
      dataRaw: JSON.stringify(data, null, 2)
    });
    
    // 从统一格式中提取结果内容
    const resultContent = data?.artifacts?.result_content || 
                         data?.result_content || 
                         '';
    
    console.log('🔍 PTM parsePredictionResults - 结果内容:', {
      resultContent,
      resultContentLength: resultContent ? resultContent.length : 0,
      resultContentPreview: resultContent ? resultContent.substring(0, 200) : 'N/A'
    });
    
    // 直接尝试解析结构化数据（优先处理新的多序列格式）
    if (data?.results && Array.isArray(data.results)) {
      console.log('🔄 PTM parsePredictionResults - 使用新的多序列结构解析');
      return parseStructuredPTMData(data);
    }
    
    // 如果有旧格式的 result_content，使用传统解析方式
    if (resultContent) {
      console.log('🔄 PTM parsePredictionResults - 使用传统result_content解析');
      // 继续使用下面的传统解析逻辑
    } else {
      console.log('❌ PTM parsePredictionResults - 没有可用的数据结构');
      return [];
    }

    const lines = resultContent.split('\n');
    const sequences: SequenceData[] = [];
    const originalSequences = parseOriginalSequences();
    let currentSequence: SequenceData | null = null;
    let siteIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // 跳过标题行
      if (line.startsWith('Position\tResidue\tPTMscores')) {
        continue;
      }

      // 处理序列标识符
      if (line.startsWith('>')) {
        if (currentSequence) {
          sequences.push(currentSequence);
        }
        const sequenceId = line.substring(1).trim();
        const originalSequence = originalSequences[sequenceId] || 
                                originalSequences['Analysis'] || 
                                originalSequences['PTM_Prediction'] || 
                                '';
        
        console.log('🔍 PTM parsePredictionResults - 为序列设置originalSequence:', {
          sequenceId,
          originalSequence: originalSequence ? originalSequence.substring(0, 50) + '...' : 'N/A',
          availableKeys: Object.keys(originalSequences)
        });
        
        currentSequence = {
          sequenceId,
          sites: [],
          originalSequence
        };
        continue;
      }

      // 处理预测位点数据
      if (currentSequence) {
        const parts = line.split('\t');
        if (parts.length >= 4) {
          const position = parseInt(parts[0].trim(), 10);
          const residue = parts[1].trim();
          const scoreField = parts[2].trim(); // PTMType:score
          const predictionField = parts[3].trim(); // None 或 PTMType:score

          // 提取分数
          const scoreParts = scoreField.split(':');
          const score = scoreParts.length > 1 ? parseFloat(scoreParts[1]) : 0;

          // 提取预测结果
          const predictionParts = predictionField.split(':');
          const prediction = predictionParts[0];

          if (!isNaN(position)) {
            currentSequence.sites.push({
              key: siteIndex++,
              position,
              residue,
              score,
              prediction
            });
          }
        }
      }
    }
    if (currentSequence) {
      sequences.push(currentSequence);
    }
    
    return sequences;
  };

  const sequences = parsePredictionResults();
  const cutoff = 0.5;
  const ptmType = getPTMType();

  // 渲染单个序列的摘要
  const renderSequenceSummary = (sites: PredictionSite[]) => {
    if (!sites || sites.length === 0) {
      return (
        <span style={{ marginLeft: 16, fontSize: '14px', color: '#999' }}>
          (无有效预测位点)
        </span>
      );
    }

    const positiveSites = sites.filter(site => site.score >= cutoff);
    const negativeSites = sites.filter(site => site.score < cutoff);

    return (
      <span style={{ marginLeft: 16, fontSize: '14px', color: '#666' }}>
        (总检测位点: <Text strong>{sites.length}</Text>, 
        阳性: <Text strong style={{ color: '#ff4d4f' }}>{positiveSites.length}</Text>, 
        阴性: <Text strong style={{ color: '#52c41a' }}>{negativeSites.length}</Text>)
      </span>
    );
  };

  // 渲染带颜色的氨基酸序列
  const renderColoredSequence = (sequence: string, sites: PredictionSite[]) => {
    if (!sequence) {
      return <Text>序列信息不可用</Text>;
    }

    // 创建位置到预测结果的映射
    const positionMap: { [key: number]: PredictionSite } = {};
    if (sites && sites.length > 0) {
      sites.forEach(site => {
        positionMap[site.position] = site;
      });
    }

    return (
      <div style={{ 
        fontFamily: 'monospace', 
        fontSize: '14px', 
        lineHeight: '1.8', 
        overflowX: 'auto', 
        padding: '10px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '4px',
        whiteSpace: 'pre-wrap', // 自动换行
        wordBreak: 'break-all', // 单词拆分
      }}>
        {sequence.split('').map((aa, index) => {
          const position = index + 1;
          const site = positionMap[position];
          
          let color = '#000000'; // 默认黑色（未参与预测）
          let backgroundColor = 'transparent';
          let fontWeight = 'normal';
          
          if (site) {
            const isPositive = site.score >= cutoff;
            color = isPositive ? '#ff4d4f' : '#52c41a'; // 阳性红色，阴性绿色
            backgroundColor = isPositive ? 'rgba(255, 77, 79, 0.1)' : 'rgba(82, 196, 26, 0.1)';
            fontWeight = 'bold';
          }
          
          return (
            <span 
              key={index} 
              style={{ 
                color,
                fontWeight,
                backgroundColor,
                padding: '2px 1px',
                borderRadius: '2px'
              }}
              title={site ? `位置: ${position}, 氨基酸: ${aa}, 得分: ${site.score.toFixed(3)}, 状态: ${site.score >= cutoff ? '阳性' : '阴性'}` : `位置: ${position}, 氨基酸: ${aa}`}
            >
              {aa}
            </span>
          );
        })}
      </div>
    );
  };

  // 渲染单个序列的详细结果表格
  const renderSequenceDetailed = (seq: SequenceData) => {
    const columns = [
      {
        title: '位置',
        dataIndex: 'position',
        key: 'position',
        sorter: (a: PredictionSite, b: PredictionSite) => a.position - b.position,
      },
      {
        title: '残基',
        dataIndex: 'residue',
        key: 'residue',
      },
      {
        title: '得分',
        dataIndex: 'score',
        key: 'score',
        sorter: (a: PredictionSite, b: PredictionSite) => a.score - b.score,
        render: (score: number) => score.toFixed(3),
      },
      {
        title: '预测结果',
        dataIndex: 'prediction',
        key: 'prediction',
      },
      {
        title: '状态',
        key: 'status',
        render: (_: any, record: PredictionSite) => {
          const isPositive = record.score >= cutoff;
          return <Tag color={isPositive ? 'red' : 'green'}>{isPositive ? '阳性' : '阴性'}</Tag>;
        }
      }
    ];

    return (
      <div>
        {/* 序列显示 */}
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>序列信息 (红色表示阳性位点, 绿色表示阴性位点)</Title>
          {renderColoredSequence(seq.originalSequence || '', seq.sites)}
        </div>
        
        {/* 详细预测结果表格 */}
        <Title level={5}>详细预测结果</Title>
        <Table
          columns={columns}
          dataSource={seq.sites}
          pagination={false}
          size="small"
          bordered
        />
      </div>
    );
  };

  return (
    <BaseResultDisplay
      title={title || `PTM预测结果 (${ptmType})`}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      data={data}
    >
      <Paragraph>
        <Text strong>PTM类型：</Text> {ptmType}
      </Paragraph>
      <Paragraph>
        <Text strong>预测阈值：</Text> {cutoff}
      </Paragraph>
      
      {sequences.length > 0 ? (
        <div>
          {sequences.map((seq, index) => (
            <div key={index}>
              {/* 序列摘要和序列显示 - 默认显示 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>{`序列 ${seq.sequenceId}`}</Text>
                  {renderSequenceSummary(seq.sites)}
                </div>
                
                {/* 序列信息显示 */}
                <div style={{ marginBottom: 16 }}>
                  <Title level={5}>序列信息 (红色表示阳性位点, 绿色表示阴性位点)</Title>
                  {renderColoredSequence(seq.originalSequence || '', seq.sites)}
                </div>
              </div>
              
              {/* 详细预测结果 - 折叠显示 */}
              <Collapse>
                <Panel header="详细预测结果" key="detailed">
                  <Table
                    columns={[
                      {
                        title: '位置',
                        dataIndex: 'position',
                        key: 'position',
                        sorter: (a: PredictionSite, b: PredictionSite) => a.position - b.position,
                      },
                      {
                        title: '残基',
                        dataIndex: 'residue',
                        key: 'residue',
                      },
                      {
                        title: '得分',
                        dataIndex: 'score',
                        key: 'score',
                        sorter: (a: PredictionSite, b: PredictionSite) => a.score - b.score,
                        render: (score: number) => score.toFixed(3),
                      },
                      {
                        title: '预测结果',
                        dataIndex: 'prediction',
                        key: 'prediction',
                      },
                      {
                        title: '状态',
                        key: 'status',
                        render: (_: any, record: PredictionSite) => {
                          const isPositive = record.score >= cutoff;
                          return <Tag color={isPositive ? 'red' : 'green'}>{isPositive ? '阳性' : '阴性'}</Tag>;
                        }
                      }
                    ]}
                    dataSource={seq.sites}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Panel>
              </Collapse>
            </div>
          ))}
        </div>
      ) : (
        <Paragraph style={{ textAlign: 'center', color: '#999' }}>未检测到{ptmType}位点</Paragraph>
      )}
    </BaseResultDisplay>
  );
};

export default PTMPredictionResult;